import React, { useState } from 'react'
import styled from 'styled-components'
import { Dialog } from '@mui/material'
import { toast } from 'react-toastify'
import SearchableSelect from 'components/SearchableSelect'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { getApiErrors } from 'utils/errors'
import { font, ink, line, radius, surface } from 'features/dashboard/tokens'

const CYAN = '#33becc'

const BulkShareEstateFilesToBpsModal = ({ estateId, fileIds, buildingPassports = [], onClose, onShared }) => {
  const [selectedBpIds, setSelectedBpIds] = useState([])
  const [busy, setBusy] = useState(false)
  const count = fileIds.length

  const bpOptions = buildingPassports.map(bp => ({
    value: bp.id,
    label: bp.name || bp.address || 'Building Passport'
  }))

  const submit = async () => {
    if (selectedBpIds.length === 0) { toast.error('Select at least one Building Passport'); return }
    setBusy(true)
    try {
      const { data } = await estateDocumentsService.bulkShareEstateFilesToBps({
        estateId,
        fileIds,
        buildingPassportIds: selectedBpIds
      })
      const copies = data?.data?.copies ?? 0
      toast.success(`Added ${count} document${count === 1 ? '' : 's'} to ${selectedBpIds.length} Building Passport${selectedBpIds.length === 1 ? '' : 's'} (${copies} cop${copies === 1 ? 'y' : 'ies'})`)
      onShared && onShared()
      onClose()
    } catch (error) {
      const apiError = getApiErrors(error)
      toast.error(apiError?.trim() ? apiError : 'Failed to add to Building Passports')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth='sm' fullWidth>
      <Container>
        <Title>Add {count} document{count === 1 ? '' : 's'} to Building Passports</Title>
        <Body>A copy of each selected document will be added to every chosen Building Passport's files.</Body>

        {buildingPassports.length === 0 ? (
          <Empty>There are no Building Passports in this estate yet.</Empty>
        ) : (
          <Field>
            <FieldLabel htmlFor='estate-bulk-share-bps'>Building Passports</FieldLabel>
            <SearchableSelect
              id='estate-bulk-share-bps'
              value={selectedBpIds}
              onChange={setSelectedBpIds}
              options={bpOptions}
              multiple
              placeholder='Choose Building Passports…'
              searchPlaceholder='Search Building Passports…'
              ariaLabel='Building Passports to add these documents to'
            />
          </Field>
        )}

        <Footer>
          <CancelButton type='button' onClick={onClose} disabled={busy}>Cancel</CancelButton>
          {buildingPassports.length > 0 && (
            <SaveButton type='button' onClick={submit} disabled={busy || selectedBpIds.length === 0}>
              {busy ? 'Adding…' : `Add to ${selectedBpIds.length || ''} Building Passport${selectedBpIds.length === 1 ? '' : 's'}`.trim()}
            </SaveButton>
          )}
        </Footer>
      </Container>
    </Dialog>
  )
}

export default BulkShareEstateFilesToBpsModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
`

const Body = styled.p`
  margin: 0;
  font-size: ${font.body};
  color: ${ink.mute};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const Empty = styled.p`
  margin: 0;
  padding: 10px 0;
  font-size: ${font.body};
  color: ${ink.mute};
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: color 160ms ease;

  &:hover:not(:disabled) {
    color: ${ink.base};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const SaveButton = styled.button`
  min-height: 40px;
  padding: 0 22px;
  border: none;
  border-radius: ${radius.sm};
  background: ${CYAN};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: background 160ms ease, opacity 160ms ease;

  &:hover:not(:disabled) {
    background: #2aa6b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
